import type { ActivityContext, ActivityHandle } from '../../core/activity';
import levelsData from './levels.json';

interface Level {
  level: number;
  objective: string;
  minGoal: number;
  maxGoal: number;
  maxCellValue: number;
  spawnMonsters: boolean;
}

const LEVELS = levelsData.levels as Level[];
const GRID_SIZE = 6;
const TOTAL_CELLS = GRID_SIZE * GRID_SIZE;
const CELL_SIZE = 60;
const MAX_LIVES = 3;
const MAX_MONSTERS = 3;
const MONSTER_MOVE_INTERVAL = 1400;
const MONSTER_SPAWN_MIN = 3500;
const MONSTER_SPAWN_MAX = 8000;
const SUB_LEVELS_PER_LEVEL = 5;

type Direction = 'up' | 'down' | 'left' | 'right';

interface Monster { pos: number; }
interface CellState { value: number; eaten: boolean; }

function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function factorsOf(n: number): number[] {
  const out: number[] = [];
  for (let i = 1; i <= n; i++) if (n % i === 0) out.push(i);
  return out;
}

function generateGridForGoal(goal: number, maxValue: number): CellState[] {
  const factors = factorsOf(goal).filter((f) => f <= maxValue);
  const cells: CellState[] = [];
  for (let i = 0; i < TOTAL_CELLS; i++) {
    if (Math.random() < 0.45 && factors.length > 0) {
      // fator válido
      cells.push({ value: factors[randInt(0, factors.length - 1)]!, eaten: false });
    } else {
      // não-fator
      let v = randInt(2, maxValue);
      let safety = 20;
      while (goal % v === 0 && safety-- > 0) v = randInt(2, maxValue);
      cells.push({ value: v, eaten: false });
    }
  }
  // garante pelo menos 4 fatores no grid
  let correctCount = cells.filter((c) => goal % c.value === 0).length;
  let i = 0;
  while (correctCount < 4 && i < TOTAL_CELLS && factors.length > 0) {
    if (goal % cells[i]!.value !== 0) {
      cells[i] = { value: factors[randInt(0, factors.length - 1)]!, eaten: false };
      correctCount++;
    }
    i++;
  }
  return cells;
}

export function mountGnumchFactors(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0]!;

  let subIndex = 0;
  let correctSubs = 0;
  let cells: CellState[] = [];
  let muncherPos = 0;
  let lives = MAX_LIVES;
  let monsters: Monster[] = [];
  let buttonsBlocked = false;
  let monsterMoveTimer: number | null = null;
  let monsterSpawnTimer: number | null = null;
  let flashTimer: number | null = null;
  let currentGoal = 0;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Comilão de fatores</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage gnumch">
      <div class="feedback" aria-live="polite"></div>
      <div class="gnumch__header">
        <p class="gnumch__progress" aria-live="polite"></p>
        <span class="gnumch__lives" aria-live="polite"></span>
      </div>
      <div class="gnumch__goal" aria-live="polite"></div>
      <div class="gnumch__grid-wrap"></div>
      <p class="gnumch__hint">${level.objective} · Coma só os números que dividem o alvo.</p>
    </section>
  `;
  ctx.root.appendChild(page);

  let progressEl = page.querySelector<HTMLElement>('.gnumch__progress')!;
  let livesEl = page.querySelector<HTMLElement>('.gnumch__lives')!;
  let goalEl = page.querySelector<HTMLElement>('.gnumch__goal')!;
  let gridWrap = page.querySelector<HTMLElement>('.gnumch__grid-wrap')!;
  let feedback = page.querySelector<HTMLElement>('.feedback')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());

  const onKey = (e: KeyboardEvent): void => {
    if (buttonsBlocked) return;
    if (e.key === 'ArrowUp') { tryMove('up'); e.preventDefault(); }
    else if (e.key === 'ArrowDown') { tryMove('down'); e.preventDefault(); }
    else if (e.key === 'ArrowLeft') { tryMove('left'); e.preventDefault(); }
    else if (e.key === 'ArrowRight') { tryMove('right'); e.preventDefault(); }
    else if (e.key === ' ' || e.key === 'Enter') { eatCurrent(); e.preventDefault(); }
  };
  window.addEventListener('keydown', onKey);

  function startSubLevel(): void {
    clearTimers();
    currentGoal = randInt(level.minGoal, level.maxGoal);
    cells = generateGridForGoal(currentGoal, level.maxCellValue);
    muncherPos = Math.floor(TOTAL_CELLS / 2) + Math.floor(GRID_SIZE / 2);
    lives = MAX_LIVES;
    monsters = [];
    buttonsBlocked = false;
    renderAll();
    if (level.spawnMonsters) {
      monsterMoveTimer = window.setInterval(tickMonsters, MONSTER_MOVE_INTERVAL);
      scheduleMonsterSpawn();
    }
  }

  function clearTimers(): void {
    if (monsterMoveTimer !== null) { window.clearInterval(monsterMoveTimer); monsterMoveTimer = null; }
    if (monsterSpawnTimer !== null) { window.clearTimeout(monsterSpawnTimer); monsterSpawnTimer = null; }
    if (flashTimer !== null) { window.clearTimeout(flashTimer); flashTimer = null; }
  }

  function scheduleMonsterSpawn(): void {
    const delay = MONSTER_SPAWN_MIN + Math.random() * (MONSTER_SPAWN_MAX - MONSTER_SPAWN_MIN);
    monsterSpawnTimer = window.setTimeout(() => {
      if (!level.spawnMonsters || buttonsBlocked) return;
      spawnMonster();
      scheduleMonsterSpawn();
    }, delay);
  }

  function spawnMonster(): void {
    if (monsters.length >= MAX_MONSTERS) return;
    const edges = [
      ...Array.from({ length: GRID_SIZE }, (_, i) => i),
      ...Array.from({ length: GRID_SIZE }, (_, i) => i + (GRID_SIZE - 1) * GRID_SIZE),
      ...Array.from({ length: GRID_SIZE }, (_, i) => i * GRID_SIZE),
      ...Array.from({ length: GRID_SIZE }, (_, i) => i * GRID_SIZE + GRID_SIZE - 1),
    ];
    const pos = edges[Math.floor(Math.random() * edges.length)]!;
    if (pos === muncherPos) return;
    monsters.push({ pos });
    if (pos === muncherPos) { onCaught(); return; }
    renderAll();
  }

  function tickMonsters(): void {
    if (buttonsBlocked) return;
    for (const m of monsters) {
      const dirs: Direction[] = ['up', 'down', 'left', 'right'];
      const shuffled = dirs.slice().sort(() => Math.random() - 0.5);
      for (const d of shuffled) {
        const next = neighbor(m.pos, d);
        if (next !== -1) { m.pos = next; break; }
      }
    }
    if (monsters.some((m) => m.pos === muncherPos)) { onCaught(); return; }
    renderAll();
  }

  function neighbor(pos: number, dir: Direction): number {
    const row = Math.floor(pos / GRID_SIZE);
    const col = pos % GRID_SIZE;
    if (dir === 'up') return row > 0 ? pos - GRID_SIZE : -1;
    if (dir === 'down') return row < GRID_SIZE - 1 ? pos + GRID_SIZE : -1;
    if (dir === 'left') return col > 0 ? pos - 1 : -1;
    return col < GRID_SIZE - 1 ? pos + 1 : -1;
  }

  function isAdjacent(a: number, b: number): boolean {
    const r1 = Math.floor(a / GRID_SIZE);
    const c1 = a % GRID_SIZE;
    const r2 = Math.floor(b / GRID_SIZE);
    const c2 = b % GRID_SIZE;
    return Math.abs(r1 - r2) + Math.abs(c1 - c2) === 1;
  }

  function tryMove(dir: Direction): void {
    if (buttonsBlocked) return;
    const next = neighbor(muncherPos, dir);
    if (next === -1) return;
    muncherPos = next;
    ctx.audio.play('click');
    if (monsters.some((m) => m.pos === muncherPos)) { onCaught(); return; }
    renderAll();
  }

  function eatCurrent(): void {
    if (buttonsBlocked) return;
    const cell = cells[muncherPos]!;
    if (cell.eaten) return;
    const correct = currentGoal % cell.value === 0;
    cell.eaten = true;
    if (correct) {
      ctx.audio.play('correct');
      flashCell(muncherPos, 'correct');
      if (allCorrectEaten()) {
        buttonsBlocked = true;
        clearTimers();
        window.setTimeout(() => {
          subIndex++;
          correctSubs++;
          if (subIndex < SUB_LEVELS_PER_LEVEL) startSubLevel();
          else finishLevel();
        }, 500);
      } else {
        renderAll();
      }
    } else {
      ctx.audio.play('wrong');
      lives--;
      flashCell(muncherPos, 'wrong');
      if (lives <= 0) onAllLivesLost();
      else renderAll();
    }
  }

  function flashCell(pos: number, kind: 'correct' | 'wrong'): void {
    renderAll();
    const cellEl = gridWrap.querySelector<SVGElement>(`rect[data-id="${pos}"]`);
    if (cellEl) cellEl.classList.add(`gnumch__cell-bg--${kind}`);
    if (flashTimer !== null) window.clearTimeout(flashTimer);
    flashTimer = window.setTimeout(() => {
      flashTimer = null;
      if (cellEl) cellEl.classList.remove(`gnumch__cell-bg--${kind}`);
    }, 350);
  }

  function allCorrectEaten(): boolean {
    return cells.every((c) => c.eaten || currentGoal % c.value !== 0);
  }

  function onCaught(): void {
    if (buttonsBlocked) return;
    lives--;
    ctx.audio.play('wrong');
    showFeedback('err');
    if (lives <= 0) onAllLivesLost();
    else {
      monsters = [];
      muncherPos = Math.floor(TOTAL_CELLS / 2) + Math.floor(GRID_SIZE / 2);
      renderAll();
    }
  }

  function onAllLivesLost(): void {
    buttonsBlocked = true;
    clearTimers();
    showFeedback('err');
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    window.setTimeout(() => {
      stage.innerHTML = `
        <h2 class="activity-finish__title">Você perdeu as 3 vidas</h2>
        <p class="activity-finish__text">Sub-nível ${subIndex + 1} de ${SUB_LEVELS_PER_LEVEL}. Tente de novo!</p>
        <div class="activity-finish__actions">
          <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
          <button class="btn btn--primary" type="button" data-act="retry">Tentar de novo</button>
        </div>
      `;
      stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
      stage.querySelector<HTMLElement>('[data-act="retry"]')!.addEventListener('click', () => {
        rebuildPage();
        startSubLevel();
      });
    }, 800);
  }

  function rebuildPage(): void {
    page.innerHTML = `
      <header class="activity-bar">
        <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
        <div class="activity-bar__title">Comilão de fatores</div>
        <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
      </header>
      <section class="activity-stage gnumch">
        <div class="feedback" aria-live="polite"></div>
        <div class="gnumch__header">
          <p class="gnumch__progress" aria-live="polite"></p>
          <span class="gnumch__lives" aria-live="polite"></span>
        </div>
        <div class="gnumch__goal" aria-live="polite"></div>
        <div class="gnumch__grid-wrap"></div>
        <p class="gnumch__hint">${level.objective} · Coma só os números que dividem o alvo.</p>
      </section>
    `;
    progressEl = page.querySelector<HTMLElement>('.gnumch__progress')!;
    livesEl = page.querySelector<HTMLElement>('.gnumch__lives')!;
    goalEl = page.querySelector<HTMLElement>('.gnumch__goal')!;
    gridWrap = page.querySelector<HTMLElement>('.gnumch__grid-wrap')!;
    feedback = page.querySelector<HTMLElement>('.feedback')!;
    page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());
  }

  function renderProgress(): void {
    progressEl.textContent = `Sub-nível ${subIndex + 1} de ${SUB_LEVELS_PER_LEVEL}`;
    livesEl.textContent = '❤️'.repeat(lives) + '🤍'.repeat(MAX_LIVES - lives);
    goalEl.innerHTML = `Fatores de <strong>${currentGoal}</strong>`;
  }

  function renderAll(): void {
    renderProgress();
    renderGrid();
  }

  function renderGrid(): void {
    const dim = GRID_SIZE * CELL_SIZE;
    gridWrap.innerHTML = '';
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', `0 0 ${dim} ${dim}`);
    svg.setAttribute('class', 'gnumch__grid');

    for (let id = 0; id < TOTAL_CELLS; id++) {
      const row = Math.floor(id / GRID_SIZE);
      const col = id % GRID_SIZE;
      const x = col * CELL_SIZE;
      const y = row * CELL_SIZE;
      const cell = cells[id]!;
      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('x', String(x));
      rect.setAttribute('y', String(y));
      rect.setAttribute('width', String(CELL_SIZE));
      rect.setAttribute('height', String(CELL_SIZE));
      let cls = 'gnumch__cell-bg';
      if (cell.eaten) cls += ' gnumch__cell-bg--eaten';
      else if (id === muncherPos) cls += ' gnumch__cell-bg--muncher';
      else if (isAdjacent(id, muncherPos)) cls += ' gnumch__cell-bg--adjacent';
      rect.setAttribute('class', cls);
      rect.setAttribute('data-id', String(id));
      rect.addEventListener('click', () => onCellClick(id));
      svg.appendChild(rect);
      if (!cell.eaten) {
        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        text.setAttribute('x', String(x + CELL_SIZE / 2));
        text.setAttribute('y', String(y + CELL_SIZE / 2));
        text.setAttribute('class', 'gnumch__cell-expr');
        text.textContent = String(cell.value);
        svg.appendChild(text);
      }
    }
    // muncher
    {
      const row = Math.floor(muncherPos / GRID_SIZE);
      const col = muncherPos % GRID_SIZE;
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', String(col * CELL_SIZE + CELL_SIZE / 2));
      text.setAttribute('y', String(row * CELL_SIZE + CELL_SIZE / 2));
      text.setAttribute('class', 'gnumch__muncher');
      text.textContent = '😋';
      svg.appendChild(text);
    }
    // monsters
    for (const m of monsters) {
      const row = Math.floor(m.pos / GRID_SIZE);
      const col = m.pos % GRID_SIZE;
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', String(col * CELL_SIZE + CELL_SIZE / 2));
      text.setAttribute('y', String(row * CELL_SIZE + CELL_SIZE / 2));
      text.setAttribute('class', 'gnumch__monster');
      text.textContent = '👻';
      svg.appendChild(text);
    }
    gridWrap.appendChild(svg);
  }

  function onCellClick(id: number): void {
    if (buttonsBlocked) return;
    if (id === muncherPos) { eatCurrent(); return; }
    if (isAdjacent(id, muncherPos)) {
      muncherPos = id;
      ctx.audio.play('click');
      if (monsters.some((m) => m.pos === muncherPos)) { onCaught(); return; }
      renderAll();
    }
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 500);
  }

  function finishLevel(): void {
    clearTimers();
    ctx.audio.play('win');
    ctx.onComplete(level.level, correctSubs);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você completou ${correctSubs} de ${SUB_LEVELS_PER_LEVEL} desafios.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) next.addEventListener('click', () => { window.location.hash = `/gnumch-factors/${level.level + 1}`; });
  }

  startSubLevel();

  return {
    unmount(): void {
      clearTimers();
      window.removeEventListener('keydown', onKey);
      ctx.root.innerHTML = '';
    },
  };
}
